import React, { useState, useRef, useEffect } from "react";
import { motion, useReducedMotion } from "framer-motion";
import { Loader2, ArrowRight } from "lucide-react";
import api from "@/lib/api";
import { Wordmark, Eyebrow } from "@/components/Wordmark";

export const Gate = ({ onAuthed }) => {
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const reduce = useReducedMotion();
  const inputRef = useRef(null);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!password || loading) return;
    setLoading(true);
    setError("");
    try {
      await api.post("/deck/login", { password });
      onAuthed();
    } catch (err) {
      setError(
        err?.response?.data?.detail || "That password doesn't match. Try again."
      );
      setLoading(false);
      inputRef.current?.focus();
    }
  };

  const ease = [0.22, 1, 0.36, 1];
  const container = {
    hidden: {},
    show: {
      transition: { staggerChildren: reduce ? 0 : 0.1, delayChildren: 0.05 },
    },
  };
  const item = {
    hidden: { opacity: 0, y: reduce ? 0 : 16 },
    show: { opacity: 1, y: 0, transition: { duration: 0.65, ease } },
  };

  return (
    <div className="stage-wrap" data-testid="gate-screen">
      <div className="stage flex items-center">
        <motion.div
          variants={container}
          initial="hidden"
          animate="show"
          className="w-full max-w-xl px-[clamp(28px,8%,96px)]"
        >
          <motion.div variants={item} className="mb-11">
            <Wordmark size="md" />
          </motion.div>

          <motion.div variants={item} className="mb-5">
            <Eyebrow>Private deck</Eyebrow>
          </motion.div>

          <motion.h1
            variants={item}
            className="font-display font-semibold text-4xl sm:text-5xl leading-[1.02] text-ink mb-4"
          >
            Your name is on the door.
          </motion.h1>

          <motion.p
            variants={item}
            className="text-ink-soft text-base leading-relaxed mb-9 max-w-md"
          >
            This walkthrough is shared privately. Enter the password to view the
            deck.
          </motion.p>

          <motion.form variants={item} onSubmit={submit} className="max-w-md">
            <label
              htmlFor="deck-password"
              className="block font-mono text-[10px] uppercase tracking-[0.2em] text-ink-muted mb-2"
            >
              Password
            </label>
            <div className="flex flex-col sm:flex-row gap-3">
              <input
                id="deck-password"
                ref={inputRef}
                data-testid="gate-password-input"
                type="password"
                autoComplete="off"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (error) setError("");
                }}
                placeholder="••••••••••"
                aria-invalid={!!error}
                aria-describedby={error ? "gate-error" : undefined}
                className="flex-1 bg-transparent border-b border-hairline focus:border-accent text-ink font-sans text-base py-2.5 outline-none transition-colors placeholder:text-ink-muted/50"
              />
              <button
                type="submit"
                data-testid="gate-submit-button"
                disabled={loading}
                className="group inline-flex items-center justify-center gap-2 bg-ink text-paper-white font-sans text-sm font-medium px-6 py-3 rounded-full transition-all duration-300 hover:bg-accent disabled:opacity-60 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    View deck
                    <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-0.5" />
                  </>
                )}
              </button>
            </div>

            {error ? (
              <p
                id="gate-error"
                data-testid="gate-error-message"
                role="alert"
                className="mt-3 text-sm text-[#B42318] font-sans"
              >
                {error}
              </p>
            ) : null}
          </motion.form>
        </motion.div>
      </div>
    </div>
  );
};

export default Gate;
